'use client';

import { type Factiii } from '@shared/all/domains/factiiis';
import { stringToSlug } from '@shared/all/helpers';
import toast from '@shared/utils/helpers/toast';
import { getDimensionsFromUri } from '@shared/utils/helpers/uploadHelper';
import useFactiiis from '@shared/utils/hooks/useFactiiis';
import useUpload from '@shared/utils/hooks/useUpload';
import { useQueryClient } from '@tanstack/react-query';
import { Camera } from 'lucide-react';
import Image from 'next/image';
import { useState } from 'react';

import { getS3ObjectURL } from '../../utils/s3';
import { client, type RouterOutputs } from '../../utils/trpc';
import Avatar from '../common/Avatar';
import Button from '../common/Button';
import Upload from '../common/Upload';
import Modal, { ModalContent, ModalFooter, ModalHeader } from '../HOC/Modal';
import Input from '../UI/Input';

enum UploadType {
  BANNER = 'Banner',
  AVATAR = 'Avatar'
}

type Props = {
  closeHandler: () => void;
  factiiiId: number;
  spaceSlug?: string;
  username?: string;
  factiii: Factiii;
};

type FactiiiData = {
  name: string;
  description: string | null;
};

const FactiiiEdit = (props: Props) => {
  const { updateFactiiiState, factiiis } = useFactiiis(client);
  const queryClient = useQueryClient();
  const { upload, addFile, files, clear } = useUpload(client);

  const initialData: FactiiiData = {
    name: props.factiii.name,
    description: props.factiii.description ?? null
  };

  const avatarKey = files['avatar']?.uri ?? props.factiii.avatarKey ?? null;
  const bannerKey = files['banner']?.uri ?? props.factiii.bannerKey ?? null;

  const [factiiiData, setFactiiiData] = useState<FactiiiData>(initialData);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState<null | UploadType>(
    null
  );

  // Detect if any changes have been made
  const hasChanges =
    factiiiData.name !== initialData.name ||
    factiiiData.description !== initialData.description ||
    !!files['avatar'] ||
    !!files['banner'];

  const saveHandler = async () => {
    setButtonLoading(true);

    const avatarChanged = !!files['avatar'];
    const bannerChanged = !!files['banner'];

    const results = await upload();
    if ((avatarChanged || bannerChanged) && Object.keys(results).length === 0) {
      setButtonLoading(false);
      return;
    }

    const avatarId = results['avatar']?.id;
    const bannerId = results['banner']?.id;

    try {
      const [error, result] = await client.factiiis.update.mutate({
        id: props.factiiiId,
        name: factiiiData.name,
        slug: stringToSlug(factiiiData.name),
        description: factiiiData.description ?? undefined,
        avatar: avatarChanged ? avatarId : undefined,
        banner: bannerChanged ? bannerId : undefined
      });

      if (error || !result) {
        toast(error?.message || 'Failed to update factiii', '', 'error');
        setButtonLoading(false);
        return;
      }

      const storedFactiii = factiiis.find((f) => f.id === props.factiiiId);
      if (storedFactiii) {
        updateFactiiiState({ ...storedFactiii, ...result });
      }
      // Patch every cached list the factiii pages actually read —
      // ['factiiiData', ...] (UnifiedFactiiiPage) and ['factiiiItems', slug]
      // (GeneralFactiiiPage). Those queries have a 5-minute staleTime and
      // refetchOnMount disabled when server-seeded, so without this patch the
      // header keeps the old avatar/banner until a hard refresh.
      for (const prefix of [['factiiiData'], ['factiiiItems']]) {
        queryClient.setQueriesData<RouterOutputs['factiiis']['factiiiItems']>(
          { queryKey: prefix },
          (old) => {
            if (!old) return old;
            return {
              ...old,
              factiiiList: old.factiiiList.map((f) =>
                f.id === props.factiiiId ? { ...f, ...result } : f
              )
            };
          }
        );
      }

      toast('Factiii updated successfully');
      props.closeHandler();
    } catch {
      toast('Unable to update factiii', '', 'error');
    } finally {
      setButtonLoading(false);
    }
  };

  const uploadCallbackFunction = (type: UploadType) => {
    return async (file: File) => {
      const key = type === UploadType.AVATAR ? 'avatar' : 'banner';
      const uri = URL.createObjectURL(file);
      const dimensions = await getDimensionsFromUri(uri);
      addFile(key, {
        uri,
        fileName: file.name,
        width: dimensions?.width,
        height: dimensions?.height
      });
      setUploadModalOpen(null);
    };
  };

  const BannerComponent = (
    <>
      <div className="relative">
        <Button
          text="Edit Cover Photo"
          classNames="w-40 absolute top-2 w-auto right-2"
          onClick={() => setUploadModalOpen(UploadType.BANNER)}
          variant="CLEAR"
          titleElement={
            <div className="flex w-auto items-center justify-around space-x-2 text-sm font-semibold">
              <Camera height={15} width={15} />
              <p>Edit Cover Photo</p>
            </div>
          }
        />
        <Image
          className="rounded-b-xl"
          height={0}
          width={0}
          sizes="100vw"
          style={{ width: 'calc(100% + 1rem)', height: 'auto' }}
          src={
            bannerKey
              ? getS3ObjectURL(bannerKey)
              : '/images/header-image-default.jpg'
          }
          unoptimized={!!bannerKey?.startsWith('blob:')}
          alt="factiii banner"
        />
      </div>
      <div className="relative -mt-14 ml-4 flex justify-start">
        <div className="relative">
          <Avatar
            size={100}
            className="border-4 border-background-secondary"
            factiiiAvatarKey={avatarKey}
            factiiiId={props.factiiiId}
          />
          <div className="absolute bottom-2 right-0">
            <Button
              variant="CLEAR"
              onClick={() => setUploadModalOpen(UploadType.AVATAR)}
              icon={<Camera height={15} width={15} />}
              tooltip="Edit Factiii Avatar"
            />
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      <Modal closeHandler={props.closeHandler}>
        <ModalHeader closeHandler={props.closeHandler}>
          <h2>Edit Factiii</h2>
        </ModalHeader>
        <ModalContent banner={BannerComponent}>
          <Input
            type="text"
            label="Name"
            value={factiiiData.name}
            onChange={(value) =>
              setFactiiiData((prev) => ({ ...prev, name: value || '' }))
            }
          />
          <Input
            type="text"
            label="URL"
            value={`${process.env.NEXT_PUBLIC_SITE_URL}/factiiis/${stringToSlug(factiiiData.name)}`}
            disabled
          />
          <Input
            label="Description"
            type="textarea"
            value={factiiiData.description ?? ''}
            onChange={(value) =>
              setFactiiiData((prev) => ({
                ...prev,
                description: value || null
              }))
            }
          />
        </ModalContent>
        <ModalFooter>
          <div className="flex w-full items-center justify-between">
            <Button
              text="Close"
              onClick={props.closeHandler}
              variant="SECONDARY"
            />
            <div className="flex gap-2">
              {hasChanges && (
                <Button
                  text="Cancel"
                  variant="SECONDARY"
                  onClick={() => {
                    setFactiiiData(initialData);
                    clear();
                  }}
                />
              )}
              <Button
                text="Save"
                onClick={saveHandler}
                disabled={buttonLoading || !hasChanges}
                loading={buttonLoading}
              />
            </div>
          </div>
        </ModalFooter>
        {uploadModalOpen !== null && (
          <Upload
            crop={{
              enabled: true,
              aspect: uploadModalOpen === UploadType.BANNER ? 3 : 1
            }}
            title={
              uploadModalOpen === UploadType.AVATAR
                ? 'Upload Factiii Avatar'
                : 'Upload Factiii Cover Photo'
            }
            closeHandler={() => setUploadModalOpen(null)}
            callbackFn={uploadCallbackFunction(uploadModalOpen)}
            enableCamera
          />
        )}
      </Modal>
    </>
  );
};

export default FactiiiEdit;
